import { useMemo } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import {
  ALL_PERMISSIONS,
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
} from './permissions';

/**
 * Permission-checking hook driven by the current user's effective permissions
 * in the auth store. The store stays in sync with `/auth/get-profile`, so when
 * an admin changes a role's permissions the gated UI updates on the next
 * session refetch (focus / poll) without a re-login.
 *
 * @example
 * const { can } = usePermissions();
 * {can(PERMISSIONS.USER_WRITE) && <Button>Edit</Button>}
 */
export function usePermissions() {
  const permissions = useAuthStore((s) => s.permissions);

  return useMemo(
    () => ({
      /** Raw effective permission names for the current user. */
      permissions,
      /** True if the user is a super admin (holds the `*` wildcard). */
      isSuperAdmin: permissions.includes(ALL_PERMISSIONS),
      /** True if the user holds the given permission. */
      can: (required: string) => hasPermission(permissions, required),
      /** True if the user holds AT LEAST ONE of the given permissions. */
      canAny: (required: string[]) => hasAnyPermission(permissions, required),
      /** True if the user holds EVERY one of the given permissions. */
      canAll: (required: string[]) => hasAllPermissions(permissions, required),
    }),
    [permissions],
  );
}
